import { motion, useScroll, useTransform } from "motion/react";
import { useRef } from "react";

import imgLogoTeamStacy from "../assets/emblema-1.svg";
import imgLogoFolklore from "../assets/emblema-2.svg";
import imgMainImage from "../assets/main.webp";
import imgThumbnailImage from "../assets/gorra.webp";
import imgThumbnailImage1 from "../assets/burger.webp";

function TeamStacyLogo() {
  return (
    <img
      src={imgLogoTeamStacy}
      alt="Team Stacy Logo"
      className="h-auto w-[110px] object-contain sm:w-[150px] lg:w-[220px]"
    />
  );
}

function FolkloreLogo() {
  return (
    <img
      src={imgLogoFolklore}
      alt="Folklore Logo"
      className="h-auto w-[108px] object-contain sm:w-[136px] lg:w-[184px]"
    />
  );
}

interface ComunidadProps {
  className?: string;
}

export default function Comunidad({ className = "" }: ComunidadProps) {
  const sectionRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
    layoutEffect: false,
  });

  const sectionOpacity = useTransform(scrollYProgress, [0, 0.12, 0.9, 1], [0, 1, 1, 0.96]);
  const sectionScale = useTransform(scrollYProgress, [0, 0.15, 0.9, 1], [0.985, 1, 1, 0.992]);

  const textY = useTransform(scrollYProgress, [0, 1], [16, -16]);
  const mainImageY = useTransform(scrollYProgress, [0, 1], [24, -24]);
  const thumbnailsY = useTransform(scrollYProgress, [0, 1], [16, -16]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [10, -10]);

  return (
    <section
      id="comunidad"
      ref={sectionRef}
      className={`relative w-full bg-beige-500 px-4 py-20 sm:px-6 sm:py-24 lg:px-10 lg:py-28 ${className}`}
      data-name="Comunidad Section"
    >
      <motion.div
        className="mx-auto w-full max-w-[1920px]"
        style={{
          opacity: sectionOpacity,
          scale: sectionScale,
        }}
      >
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,0.96fr)_minmax(0,1.04fr)] lg:items-end lg:gap-14 xl:gap-20">
          <motion.div
            className="flex min-w-0 flex-col items-start justify-end gap-4 lg:gap-5"
            style={{ y: textY }}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.22 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.22 }}
              transition={{ duration: 0.45, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
            >
              <TeamStacyLogo />
            </motion.div>

            <motion.h2
              className="font-['Excessive_Extra_Condensed'] leading-[0.9] tracking-[0.02em] text-[#181819]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.22 }}
              transition={{ duration: 0.55, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="block text-[clamp(7rem,12vw,10.5rem)]">MÁS QUE SOLO</span>
              <span className="block text-[clamp(7rem,12vw,10.5rem)]">UN LUGAR PARA COMER</span>
            </motion.h2>

            <motion.p
              className="max-w-[36rem] font-['Roboto_Mono',monospace] text-[clamp(1.1rem,2.2vw,2rem)] leading-[1.12] tracking-[0.02em] text-[#181819]"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.22 }}
              transition={{ duration: 0.5, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
            >
              No tenemos clientes.
              <br />
              <span className="font-bold">Tenemos familia.</span>
            </motion.p>
          </motion.div>

          <motion.div
            className="flex min-w-0 flex-col gap-4 sm:gap-5 lg:gap-6"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.22 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              style={{ y: mainImageY }}
              whileHover={{ scale: 1.015 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden rounded-2xl"
            >
              <img
                src={imgMainImage}
                alt="Comunidad Stacy"
                className="h-[clamp(320px,42vw,440px)] w-full object-cover"
              />
            </motion.div>

            <motion.div
              style={{ y: thumbnailsY }}
              className="grid grid-cols-2 gap-4 sm:gap-6"
            >
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="overflow-hidden rounded-2xl"
              >
                <img
                  src={imgThumbnailImage}
                  alt="Gorra Stacy"
                  className="h-[clamp(180px,22vw,220px)] w-full object-cover"
                />
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="overflow-hidden rounded-2xl"
              >
                <img
                  src={imgThumbnailImage1}
                  alt="Burger Stacy"
                  className="h-[clamp(180px,22vw,220px)] w-full object-cover"
                />
              </motion.div>
            </motion.div>

            <motion.div
              style={{ y: badgeY }}
              className="flex items-center justify-center rounded-2xl bg-[#181819] px-6 py-5 sm:py-6"
            >
              <FolkloreLogo />
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}